import add from "date-fns/add";
import endOfMonth from "date-fns/endOfMonth";
import format from "date-fns/format";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import isSameMonth from "date-fns/isSameMonth";
import lastDayOfMonth from "date-fns/lastDayOfMonth";
import startOfMonth from "date-fns/startOfMonth";
import sub from "date-fns/sub";

export const formatToMonthDayYearDate = (
  date: string | number | Date,
): string => formatDate(date, "MMM dd, yy");

export const formatToMonthDayFullYearDate = (
  date: string | number | Date,
): string => {
  const d = new Date(date);

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isUSTimeZone = timeZone.startsWith("America/");

  if (isUSTimeZone) {
    d.setUTCDate(d.getUTCDate() + 1);
  }

  const result = formatDate(d, "MMM dd, yyyy");
  return result;
};

export const formatToDayMonthFullYearDate = (
  date: string | number | Date,
): string => formatDate(date, "dd.MM.yyyy");

export const formatToDayMonthYearDate = (
  date: string | number | Date,
): string => formatDate(date, "dd.MM.yy");

export const formatToMonthYearDate = (date: string | number | Date): string =>
  formatDate(date, "MMM, yy");

export const formatToMonthFullYearDate = (
  date: string | number | Date,
): string => formatDate(date, "MMM, yyyy");

export const formatToDayMonthFullYearTimeDate = (
  date: string | number | Date,
): string => formatDate(date, "dd.MM.yyyy, HH:mm");

export const formatToYearMonth = (date: string | number | Date): string =>
  formatDate(date, "yyyy-MM");

export const formatToYearMonthDay = (date: string | number | Date): string =>
  formatDate(date, "yyyy-MM-dd");

export const formatToLastMonthDay = (date: string | number | Date): string => {
  try {
    const parsedDate =
      typeof date === "string" ? formatStringToDate(date) : new Date(date);
    return lastDayOfMonth(parsedDate!).toISOString();
  } catch (error) {
    console.error(error);
    return "";
  }
};

export const formatToUniversalTime = (
  _date: string | number | Date,
): string => {
  try {
    const date = new Date(_date);

    return date.toString();
  } catch (error) {
    console.error(error);

    return "";
  }
};

export const formatToDistanceToNow = (
  _date: string | number | Date,
): string => {
  try {
    const date = new Date(_date);

    return formatDistanceToNow(date, { addSuffix: true });
  } catch (err) {
    console.error(err);

    return "";
  }
};

export const formatStringToDate = (
  value?: string | null,
  defaultValue: Date | null = null,
): Date | null => {
  if (!value) return defaultValue;

  // Match YYYY-MM or YYYY-MM-DD
  const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(value);
  if (match) {
    const year = parseInt(match[1] || "0", 10);
    const month = parseInt(match[2] || "1", 10) - 1; // JS months are 0-based
    const day = match[3] ? parseInt(match[3], 10) : 1;
    return new Date(year, month, day);
  }

  try {
    return new Date(value);
  } catch (error) {
    console.error(error);

    return defaultValue;
  }
};

export const cleanTimeFromDate = (date: string | number | Date): string =>
  formatDate(date, "yyyy-MM-dd");

export const checkIsSameMonth = (
  firstDate: string | number | Date,
  secondDate: string | number | Date,
): boolean => {
  try {
    const [formattedFirstDate, formattedSecondDate] = [
      new Date(firstDate),
      new Date(secondDate),
    ];

    return isSameMonth(formattedFirstDate, formattedSecondDate);
  } catch (error) {
    console.error(error);

    return false;
  }
};

export const adjustDateToUTC = (date: string | number | Date): Date | null => {
  try {
    let formattedDate = new Date(date);

    const timezoneOffset = formattedDate.getTimezoneOffset();

    if (timezoneOffset > 0)
      formattedDate = add(formattedDate, { minutes: timezoneOffset });

    if (timezoneOffset < 0)
      formattedDate = sub(formattedDate, { minutes: timezoneOffset });

    return formattedDate;
  } catch (error) {
    console.error(error);

    return null;
  }
};

export const getMonthStartDate = (
  date: string | number | Date,
): Date | null => {
  try {
    const formattedDate = new Date(date);

    const formattedStartOfMonth = startOfMonth(formattedDate);

    return adjustDateToUTC(formattedStartOfMonth);
  } catch (error) {
    console.error(error);

    return null;
  }
};

export const getMonthEndDate = (date: string | number | Date): Date | null => {
  try {
    const formattedDate = new Date(date);

    const formattedEndOfMonth = endOfMonth(formattedDate);

    return adjustDateToUTC(formattedEndOfMonth);
  } catch (error) {
    console.error(error);

    return null;
  }
};

function formatDate(_date: string | number | Date, template: string): string {
  try {
    const date = new Date(_date);

    return format(date, template);
  } catch (err) {
    console.error(err);

    return "";
  }
}
